import React, { useCallback } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { NetworkStatus } from 'apollo-client';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { TitleContentPropertyMutation } from './TitleContentPropertyMutation.experimentalgraphql.ts';

const useTitleContentPropertiesForEditorQuery = graphql`
	query useTitleContentPropertiesForEditorQuery($contentId: ID, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				pageTitlePublished: properties(key: "page-title-property-published") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				pageTitleDraft: properties(key: "page-title-property-draft") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				metadata {
					frontend {
						coverPictureWidth
					}
				}
			}
		}
	}
`;
import { contentPropertyHelper } from './contentPropertyHelper.ts';
import type { TitleContentPropertyMutation as TitleContentPropertyMutationType } from './__types__/TitleContentPropertyMutation.ts';
import type {
	useTitleContentPropertiesForEditorQuery$data,
	useTitleContentPropertiesForEditorQuery$variables,
} from './__generated__/useTitleContentPropertiesForEditorQuery.graphql.ts';
import {
	TitleAlignmentType,
	defaultPropertyValue,
	COVERPICTUREWIDTH,
} from './contentTopperConstants.ts';

type UseTitleContentPropertiesForEditorProps = {
	contentId: string;
	propertyKey?: string;
	isDelete?: boolean;
	loadTitleContentProperties?: boolean;
	updateAlignmentState?: (alignment?: TitleAlignmentType) => void;
	updateCoverPictureWidthState?: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	contentType: 'page' | 'blogpost' | null;
	flags?: FlagsStateContainer;
	updateIsLoadingComplete?: (isLoadingComplete: boolean) => void;
};

export const getPageTitleDraftTitlePropertyContent = (
	queryData: useTitleContentPropertiesForEditorQuery$data | null,
) =>
	queryData?.content?.nodes?.[0]?.pageTitleDraft?.nodes?.find(
		(node) => node?.key === 'page-title-property-draft',
	);

const i18n = defineMessages({
	errorFlagTitle: {
		id: 'content-topper.page-title.alignment.error.title',
		defaultMessage: 'We couldn’t update the title alignment',
		description:
			"Title for error flag when a user tries to change title alignment and there's an error",
	},
	errorFlagDescription: {
		id: 'content-topper.page-title.alignment.error.description',
		defaultMessage: 'Try again, or refresh the page.',
		description:
			"Description for error flag when a user tries to change title alignment and there's an error",
	},
});

export const useTitleContentPropertiesForEditor = ({
	contentId,
	propertyKey = 'page-title-property-draft',
	isDelete = false,
	loadTitleContentProperties = false,
	updateAlignmentState,
	updateCoverPictureWidthState,
	contentType,
	flags,
	updateIsLoadingComplete,
}: UseTitleContentPropertiesForEditorProps) => {
	const isInitialLoadComplete = React.useRef(false);
	const isTitleCenteredInitial = React.useRef(false);

	const handleError = useCallback(
		(error: any, suppressErrors: boolean) => {
			const isUnauthOrNotFoundError = isUnauthorizedError(error) || isNotFoundError(error);
			if (!isUnauthOrNotFoundError) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.COMPANY_HUB,
				});
			}
			markErrorAsHandled(error);
			if (!suppressErrors) {
				void flags?.showErrorFlag({
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagDescription} />,
				});
			}
		},
		[flags],
	);

	const getAlignmentFromContentProperty = (response: any): TitleAlignmentType => {
		const titlePropertyContent = getPageTitleDraftTitlePropertyContent(response);
		if (titlePropertyContent?.value) {
			try {
				return (
					contentPropertyHelper(titlePropertyContent)?.titleLayoutAlignment ||
					defaultPropertyValue.titleLayoutAlignment
				);
			} catch (e) {
				handleError(e, false);
				return defaultPropertyValue.titleLayoutAlignment;
			}
		} else {
			return defaultPropertyValue.titleLayoutAlignment;
		}
	};

	const onCompleted = (response: any) => {
		const coverPictureWidth: COVERPICTUREWIDTH =
			response.content?.nodes?.[0]?.metadata?.frontend?.coverPictureWidth || COVERPICTUREWIDTH.FULL;
		void updateAlignmentState?.(getAlignmentFromContentProperty(response));
		void updateCoverPictureWidthState?.(coverPictureWidth);
	};

	const {
		data,
		networkStatus,
		refetch: refetchProperties,
	} = useQueryInterop<
		useTitleContentPropertiesForEditorQuery$data,
		useTitleContentPropertiesForEditorQuery$variables
	>(useTitleContentPropertiesForEditorQuery, {
		variables: {
			contentId,
			status: ['current', 'draft', 'archived'],
		},
		fetchPolicy: Boolean(window.__SSR_RENDERED__) ? 'cache-first' : 'cache-and-network', // cache-and-network is required for: edit cover width, transition out of edit, transition back to edit for same page (for Live Docs and Classic pages)
		onError: (error) => {
			handleError(error, true);
		},
		skip: !(loadTitleContentProperties || Boolean(window.__SSR_RENDERED__)),
		notifyOnNetworkStatusChange: true,
	});
	const isTitleAlignmentLoading = networkStatus === NetworkStatus.loading && !data;
	const refetchLoading = networkStatus === NetworkStatus.refetch;
	const coverImageWidthCached = data?.content?.nodes?.[0]?.metadata?.frontend?.coverPictureWidth;

	isTitleCenteredInitial.current =
		(Boolean(window.__SSR_RENDERED__) || !isTitleAlignmentLoading) &&
		getAlignmentFromContentProperty(data) === TitleAlignmentType.CENTER;
	if (!isInitialLoadComplete.current && loadTitleContentProperties && data) {
		isInitialLoadComplete.current = true;
		onCompleted(data);
		updateIsLoadingComplete?.(true);
	}

	const {
		updateContentProperty,
		updateContentPropertyResult: { loading: isUpdatingAlignment } = {},
	} = useUpdateContentProperty({
		contentId,
		contentType: contentType || 'page',
	});

	const [updatePropertiesExperimental, { loading: isUpdatingAlignmentExperimental }] =
		useMutation<TitleContentPropertyMutationType>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
			TitleContentPropertyMutation,
		);

	const onPageTitlePropertyUpdatedSubscriptionData = useCallback(
		(_eventName: string, response: any) => {
			const { pageTitlePropertyUpdated } = response || {};
			try {
				const value = JSON.parse(pageTitlePropertyUpdated?.value);
				void updateAlignmentState?.(value?.titleLayoutAlignment);
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		},
		[updateAlignmentState],
	);

	useSubscriptionEvent({
		contentId,
		contentType: contentType || 'page',
		delta: 'pageTitlePropertyUpdated',
		onDataReceived: onPageTitlePropertyUpdatedSubscriptionData,
		subscriberId: 'useTitleContentPropertiesForEditor',
	});

	const updatePageTitleProperty = useCallback(
		async (propertyValue: any) => {
			try {
				const getVersionNumber = (data: any) =>
					data?.content?.nodes?.[0]?.pageTitleDraft?.nodes?.[0]?.version?.number || 0;
				const versionData = await refetchProperties();
				const versionNumber = getVersionNumber(versionData.data);
				const newVersionNumber = versionNumber ? versionNumber + 1 : 1;

				if (expValEquals('migrate_off_experimentalupdatecontentproperty', 'isEnabled', true)) {
					await updateContentProperty({
						propertyKey,
						propertyValue,
						isDelete,
						hasExistingProperty: versionNumber > 0,
						shouldOptimisticResponse: true,
						options: {
							update: () => {
								try {
									const cacheData = readQueryInterop({
										query: useTitleContentPropertiesForEditorQuery,
										variables: {
											contentId,
											status: ['current', 'draft', 'archived'],
										},
									});
									(cacheData as any).content!.nodes![0]!.pageTitleDraft!.nodes![0] = {
										...(cacheData as any).content!.nodes![0]!.pageTitleDraft!.nodes![0],
										id: contentId,
										key: propertyKey,
										value: JSON.stringify(propertyValue),
										version: {
											number: newVersionNumber,
											__typename: 'Version',
										},
									};

									writeQueryInterop({
										query: useTitleContentPropertiesForEditorQuery,
										variables: {
											contentId,
											status: ['current', 'draft', 'archived'],
										},
										data,
									});
								} catch (error) {
									throw new Error(
										` failed to write to cache while updating ${propertyKey} content property after user updated page title${error.message}`,
									);
								}
							},
						},
					});
				} else {
					const optimisticResponse = {
						__typename: 'Mutation',
						experimentalUpdateContentProperty: {
							__typename: 'experimentalUpdateContentProperty',
							success: true,
							id: contentId,
							key: propertyKey,
							value: propertyValue,
						},
					};
					await updatePropertiesExperimental({
						variables: {
							contentId,
							propertyKey,
							isDelete,
							propertyValue,
							versionNumber: String(newVersionNumber),
						},
						optimisticResponse,
						update: (proxy: any) => {
							try {
								const cacheData = proxy.readQuery({
									query: useTitleContentPropertiesForEditorQuery,
									variables: {
										contentId,
										status: ['current', 'draft', 'archived'],
									},
								});

								cacheData!.content!.nodes![0]!.pageTitleDraft!.nodes![0] = {
									...cacheData!.content!.nodes![0]!.pageTitleDraft!.nodes![0],
									id: contentId,
									key: propertyKey,
									value: JSON.stringify(propertyValue),
									version: {
										number: newVersionNumber,
										__typename: 'Version',
									},
								};

								proxy.writeQuery({
									query: useTitleContentPropertiesForEditorQuery,
									variables: {
										contentId,
										status: ['current', 'draft', 'archived'],
									},
									data,
								});
							} catch (error) {
								throw new Error(
									` failed to write to cache while updating ${propertyKey} content property after user updated page title${error.message}`,
								);
							}
						},
					});
				}
			} catch (err) {
				handleError(err, false);
			}
		},
		[
			propertyKey,
			contentId,
			isDelete,
			updateContentProperty,
			updatePropertiesExperimental,
			refetchProperties,
			data,
			handleError,
		],
	);

	return {
		isUpdatingAlignment: isUpdatingAlignmentExperimental || isUpdatingAlignment || refetchLoading,
		updatePageTitleProperty,
		// exported for testing purposes
		onCompleted,
		isTitleAlignmentLoading,
		isTitleCenteredInitial: isTitleCenteredInitial.current,
		coverImageWidthCached: coverImageWidthCached as COVERPICTUREWIDTH,
	};
};
